import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';

import PropTypes from 'prop-types';
import MultiLineGraph from '../../d3/MultiLineGraph';
import useFetchChannelKeyGraphData from './useFetchChannelKeyGraphData';
import D3Utility from '../../d3/D3Utility';

import envConfig from '../../../envConfig';
import { sessionHash } from '../../../utility/remoteServiceUtil';
import useResizeObserver from '../../hooks/useResizeObserver/useResizeObserver';
import { selectSessionInformation } from '../../../redux/slices/sessionSlice';

// eslint-disable-next-line no-unused-vars
function MultiAlertGraph({
  channelKeys, alertConfig, colors, showDateToggle = true,
}) {
  const {
    graphData,
    setGraphData,
    fetchGraphData,
  } = useFetchChannelKeyGraphData();

  const graphContainerRef = useRef(null);
  const { width } = useResizeObserver(graphContainerRef);

  // const { timezone, temperatureScale } = useSelector((state) => state.session);
  const { timezone, temperatureScale } = useSelector(selectSessionInformation);

  const [selectedButton, setSelectedButton] = useState(2);

  const dates = useMemo(() => {
    const now = Date.now(); // milliseconds

    if (!showDateToggle) {
      return {
        startTime: now - 24 * 60 * 60 * 1000,
        endTime: now,
      };
    }

    return D3Utility.getDates(selectedButton);
  }, [showDateToggle, selectedButton]);

  // Load ALL channel keys on mount
  useEffect(() => {
    fetchGraphData(channelKeys, dates.startTime, dates.endTime);
  }, [channelKeys]);

  // button change handler
  const handleTimeRangeChange = (selection) => {
    const time = D3Utility.getDates(selection);
    setGraphData({
      ...graphData,
      toGraph: D3Utility.getDataInRange(graphData.originalData, time.startTime),
      startTime: time.startTime,
      endTime: time.endTime,
    });
    setSelectedButton(selection);
  };

  const dataSets = channelKeys.map((key) => ({
    id: key,
    color: colors[key],
    data: graphData.toGraph
      .filter((d) => d.channelKey === key)
      .map((d) => ({ timestamp: d.timestamp, value: d.value })),
  }));

  // ----------- CSV DOWNLOAD URL -----------
  const csvReportUrlBase = `${envConfig.apiUrl}/api/v1/device-data/device/samples/csv/find-by-channel-keys`;

  const csvHref = `${csvReportUrlBase}?auth_token=${sessionHash}`
        + `&channelKeys=${channelKeys.join(',')}`
        + `&startTime=${Math.ceil(graphData.startTime / 1000)}`
        + `&endTime=${Math.ceil(graphData.endTime / 1000)}`
        + `&temperatureScale=${temperatureScale}`
        + `&timezone=${timezone}`;

  return (
    <div ref={graphContainerRef}>
      <div className="row mb-2">
        <div className="col-auto ml-auto text-right">
          <a
            href={csvHref}
            rel="noopener noreferrer"
            download="multi-channel.csv"
            className="text-reset text-decoration-none"
          >
            <i className="fa-solid fa-file-csv" />
          </a>
        </div>

        {
          showDateToggle && (
          <div className="col-auto text-right">
            <ToggleButtonGroup
              name="timeRange"
              onChange={handleTimeRangeChange}
              style={{ width: '232px' }}
              size="sm"
            >
              <ToggleButton value={0} variant={selectedButton === 0 ? 'primary' : 'outline-secondary'}>
                24 Hour
              </ToggleButton>
              <ToggleButton value={1} variant={selectedButton === 1 ? 'primary' : 'outline-secondary'}>
                48 Hour
              </ToggleButton>
              <ToggleButton value={2} variant={selectedButton === 2 ? 'primary' : 'outline-secondary'}>
                1 Week
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
          )
        }

      </div>

      <MultiLineGraph
        dataSets={dataSets}
        temperatureScale={temperatureScale}
        threshold={alertConfig.threshold}
        startTime={graphData.startTime}
        endTime={graphData.endTime}
        timeZone={timezone}
        channelKeyObject={alertConfig.channelKeyObject}
        customUnits={alertConfig.customUnits}
        width={Math.max(width, 300)}
        height={284}
      />
    </div>
  );
}

MultiAlertGraph.defaultProps = {
  showDateToggle: true,
};
MultiAlertGraph.propTypes = {
  channelKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  alertConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  colors: PropTypes.objectOf(PropTypes.string).isRequired,
  showDateToggle: PropTypes.bool,
};
export default MultiAlertGraph;
